import React, { useState } from 'react'

export default function ContactForm({ title = 'Få et uforpligtende tilbud', subtitle = 'Udfyld formularen, så vender vi tilbage inden for 24 timer med et skræddersyet tilbud.' }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '', // Honeypot
  })

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null,
  })

  const [errors, setErrors] = useState({})

  const validate = () => {
    const newErrors = {}
    if (!formData.name.trim()) newErrors.name = 'Navn er påkrævet'
    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefonnummer er påkrævet'
    } else if (!/^[0-9\s+]{8,15}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Indtast venligst et gyldigt telefonnummer'
    }
    if (!formData.email.trim()) {
      newErrors.email = 'E-mail er påkrævet'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Indtast venligst en gyldig e-mailadresse'
    }
    if (!formData.description.trim()) newErrors.description = 'Beskrivelse er påkrævet'
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return

    setStatus({ submitting: true, success: false, error: null })

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      if (response.ok) {
        setStatus({ submitting: false, success: true, error: null })
        setFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: '',
        })
      } else {
        const errData = await response.json().catch(() => ({}))
        setStatus({
          submitting: false,
          success: false,
          error: errData.message || 'Der skete en fejl under afsendelsen. Prøv venligst igen.',
        })
      }
    } catch (err) {
      setStatus({
        submitting: false,
        success: false,
        error: 'Netværksfejl. Kontroller din internetforbindelse og prøv igen.',
      })
    }
  }

  return (
    <div className="rounded-none border border-brand-border bg-white p-6 md:p-8 shadow-lg">
      <h3 className="text-xl md:text-2xl font-bold text-brand-navy tracking-tight">{title}</h3>
      <p className="mt-2 text-sm text-brand-text-muted leading-relaxed">{subtitle}</p>

      {status.success ? (
        <div className="mt-6 rounded-none bg-emerald-50 border border-emerald-200 p-6 text-center animate-in fade-in duration-300">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 mb-4">
            <i className="fa-solid fa-circle-check text-2xl"></i>
          </div>
          <h4 className="text-lg font-bold text-emerald-900">Tak for din henvendelse!</h4>
          <p className="mt-2 text-sm text-emerald-700">
            Vi har modtaget din besked og vil kontakte dig hurtigst muligt, typisk inden for 24 timer på hverdage.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          {/* Honeypot field - absolutely hidden from humans */}
          <div className="sr-only absolute" aria-hidden="true">
            <label htmlFor="website_url">Do not fill this out if you are human</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-semibold text-brand-text-dark">
              Navn <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`mt-1.5 w-full rounded-none border px-4 py-3 text-sm transition-all duration-200 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 focus:outline-none ${
                errors.name ? 'border-red-500 bg-red-50/50' : 'border-brand-border bg-brand-light/50'
              }`}
              placeholder="Dit fulde navn"
            />
            {errors.name && <p className="mt-1 text-xs font-medium text-red-600">{errors.name}</p>}
          </div>

          {/* Phone & Email Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-brand-text-dark">
                Telefonnummer <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`mt-1.5 w-full rounded-none border px-4 py-3 text-sm transition-all duration-200 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 focus:outline-none ${
                  errors.phone ? 'border-red-500 bg-red-50/50' : 'border-brand-border bg-brand-light/50'
                }`}
                placeholder="E.g. 12 34 56 78"
              />
              {errors.phone && <p className="mt-1 text-xs font-medium text-red-600">{errors.phone}</p>}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-brand-text-dark">
                E-mail <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`mt-1.5 w-full rounded-none border px-4 py-3 text-sm transition-all duration-200 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 focus:outline-none ${
                  errors.email ? 'border-red-500 bg-red-50/50' : 'border-brand-border bg-brand-light/50'
                }`}
                placeholder="din@email.dk"
              />
              {errors.email && <p className="mt-1 text-xs font-medium text-red-600">{errors.email}</p>}
            </div>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-semibold text-brand-text-dark">
              Beskrivelse af opgaven <span className="text-red-500">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              rows="4"
              value={formData.description}
              onChange={handleChange}
              className={`mt-1.5 w-full rounded-none border px-4 py-3 text-sm transition-all duration-200 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 focus:outline-none ${
                errors.description ? 'border-red-500 bg-red-50/50' : 'border-brand-border bg-brand-light/50'
              }`}
              placeholder="Beskriv hvad du ønsker hjælp til (f.eks. antal rum, kvadratmeter, hyppighed)..."
            ></textarea>
            {errors.description && <p className="mt-1 text-xs font-medium text-red-600">{errors.description}</p>}
          </div>

          {status.error && (
            <div className="rounded-none bg-red-50 border border-red-200 p-4 text-sm text-red-700 flex items-start gap-2.5">
              <i className="fa-solid fa-circle-exclamation mt-0.5 text-base"></i>
              <span>{status.error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={status.submitting}
            className="w-full rounded-none bg-brand-navy py-3.5 text-sm font-bold text-white transition-all duration-200 hover:bg-brand-darkblue disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
          >
            {status.submitting ? (
              <>
                <i className="fa-solid fa-spinner animate-spin"></i>
                Sender tilbud...
              </>
            ) : (
              <>
                <i className="fa-solid fa-paper-plane"></i>
                Send forespørgsel
              </>
            )}
          </button>
        </form>
      )}
    </div>
  )
}
